/**
 * DiagramsInterface Component
 *
 * The Diagram_Browser at `/diagrams`: a Card-layout page (per
 * `.kiro/steering/components.md`) with a collapsible folder sidebar, a toolbar
 * (search, view selector, sort, and a New diagram button), and either a
 * thumbnail grid (default) or a table of diagrams. It owns the browser's URL
 * state (`folder`, `view`, `search`, `sort`) and the loading, empty, and error
 * states. See Requirements 9.1, 9.2, 9.3, 9.4, 9.6, and 9.9.
 *
 * The "New diagram" button opens the `NewDiagramDialog` (task 5.3), which
 * reserves an id, creates the diagram, and navigates to the editor; a parent
 * may override that with an `onNewDiagram` callback. The per-diagram actions
 * menu (task 5.4) is still a separate task: this component passes an optional
 * `renderActions` through to the grid and table for it. Opening a diagram
 * navigates to the editor route (task 5.1).
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ArrowDownUp, Filter, LayoutGrid, Plus, Table2 } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Item, ItemContent, ItemTitle } from "@/components/ui/item"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import SearchButton from "@/components/shared/SearchButton"
import { useAuth } from "@/hooks/useAuth"
import {
  listDiagrams,
  type DiagramSummary,
  type ListDiagramsResponse,
  type RepoInfo,
} from "@/services/diagramsService"
import DiagramsSidebar from "@/components/diagrams/DiagramsSidebar"
import DiagramGrid from "@/components/diagrams/DiagramGrid"
import DiagramTable from "@/components/diagrams/DiagramTable"
import DiagramActionsMenu from "@/components/diagrams/DiagramActionsMenu"
import NewDiagramDialog from "@/components/diagrams/NewDiagramDialog"
import {
  buildFolderEntries,
  DEFAULT_KIND_FILTER,
  DEFAULT_SORT,
  DEFAULT_VIEW,
  FOLDER_ALL,
  FOLDER_UNFILED,
  parseKindFilter,
  parseSort,
  parseView,
  selectDiagrams,
  type DiagramKindFilter,
  type DiagramSortKey,
  type DiagramView,
  type FolderEntry,
} from "@/components/diagrams/diagramBrowser"

/** The page title, shown in the browser tab and the card header (per components.md). */
const PAGE_TITLE: string = "Diagrams"

/** The label shown for each sort option in the sort dropdown. */
const SORT_LABELS: Record<DiagramSortKey, string> = {
  updated: "Last edited",
  title: "Title",
}

/** The label shown for each kind-filter option in the kind dropdown. */
const KIND_FILTER_LABELS: Record<DiagramKindFilter, string> = {
  all: "All kinds",
  excalidraw: "Excalidraw",
  mermaid: "Mermaid",
}

/**
 * A segmented grid/table toggle styled to match the toolbar's other buttons
 * (outline, small). Kept here rather than reusing the shared `TableViewSelector`
 * because that component's view modes and labels ("Compact"/"Expanded") do not
 * describe grid vs table; this toggle shows the two the browser actually offers.
 */
function ViewToggle({
  value,
  onChange,
}: {
  value: DiagramView
  onChange: (view: DiagramView) => void
}): React.JSX.Element {
  const options: { view: DiagramView; label: string; icon: React.ReactNode }[] = [
    { view: "grid", label: "Grid view", icon: <LayoutGrid className="size-4" aria-hidden="true" /> },
    { view: "table", label: "Table view", icon: <Table2 className="size-4" aria-hidden="true" /> },
  ]
  return (
    <div className="inline-flex items-center rounded-md border border-border" role="group" aria-label="View">
      {options.map((option) => {
        const isActive: boolean = option.view === value
        return (
          <Tooltip key={option.view}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onChange(option.view)}
                aria-label={option.label}
                aria-pressed={isActive}
                className={cn(
                  "flex h-8 w-9 items-center justify-center transition-colors first:rounded-l-md last:rounded-r-md",
                  isActive ? "bg-accent text-accent-foreground" : "hover:bg-muted",
                )}
              >
                {option.icon}
              </button>
            </TooltipTrigger>
            <TooltipContent>{option.label}</TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

/** Props for the DiagramsInterface component. */
interface DiagramsInterfaceProps {
  /**
   * Optional override for the "New diagram" button. When omitted, the button
   * opens the built-in `NewDiagramDialog`. A parent can supply this to drive
   * the create flow itself.
   */
  onNewDiagram?: () => void
  /**
   * Renders the actions menu (task 5.4) for a given diagram, shown on each grid
   * card and table row. Optional so the browser renders before that task lands.
   */
  renderActions?: (diagram: DiagramSummary) => React.ReactNode
}

/**
 * The Diagram_Browser interface.
 *
 * @param props - Component props.
 * @param props.onNewDiagram - Handler for the New diagram button (task 5.3).
 * @param props.renderActions - Optional per-diagram actions renderer (task 5.4).
 * @returns The rendered browser page.
 */
export default function DiagramsInterface({
  onNewDiagram,
  renderActions,
}: DiagramsInterfaceProps): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Whether the New diagram dialog (task 5.3) is open. The dialog navigates to
  // the editor on success, so the browser does not need to refresh here; a new
  // diagram appears in the list on the next visit or reload.
  const [newDialogOpen, setNewDialogOpen] = useState<boolean>(false)

  // Fetched data.
  const [diagrams, setDiagrams] = useState<DiagramSummary[]>([])
  // The repo info is used by the actions menu to build GitLab URLs and the
  // Embed_Snippet. Null until the first list response arrives.
  const [repo, setRepo] = useState<RepoInfo | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // URL-backed browser state. Initialised from the query string so a shared
  // link reopens the same view.
  const [selectedFolder, setSelectedFolder] = useState<string>(
    () => searchParams.get("folder") ?? FOLDER_ALL,
  )
  const [view, setView] = useState<DiagramView>(() => parseView(searchParams.get("view")))
  const [searchQuery, setSearchQuery] = useState<string>(() => searchParams.get("search") ?? "")
  const [sort, setSort] = useState<DiagramSortKey>(() => parseSort(searchParams.get("sort")))
  const [kindFilter, setKindFilter] = useState<DiagramKindFilter>(() =>
    parseKindFilter(searchParams.get("kind")),
  )

  // Keep the URL in sync with the browser state, omitting defaults so a plain
  // `/diagrams` link stays clean. Mirrors SurveyInterface's approach.
  useEffect(() => {
    const params: Record<string, string> = {}
    if (selectedFolder !== FOLDER_ALL) {
      params.folder = selectedFolder
    }
    if (view !== DEFAULT_VIEW) {
      params.view = view
    }
    if (searchQuery.trim() !== "") {
      params.search = searchQuery
    }
    if (sort !== DEFAULT_SORT) {
      params.sort = sort
    }
    if (kindFilter !== DEFAULT_KIND_FILTER) {
      params.kind = kindFilter
    }
    setSearchParams(params, { replace: true })
  }, [selectedFolder, view, searchQuery, sort, kindFilter, setSearchParams])

  const fetchDiagrams = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const token: string = await getAccessToken()
      const response: ListDiagramsResponse = await listDiagrams(token)
      setDiagrams(response.items)
      setRepo(response.repo)
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : "Failed to load diagrams")
    } finally {
      setLoading(false)
    }
  }, [getAccessToken])

  useEffect(() => {
    void fetchDiagrams()
  }, [fetchDiagrams])

  /**
   * After an import created several diagrams, show the folder they went into
   * ("Unfiled" when no folder was chosen) and refresh the list so they appear
   * (Requirement 16.6).
   *
   * @param folder - The folder the diagrams were imported into, or null.
   */
  const handleImportedSeveral = useCallback(
    (folder: string | null): void => {
      setSelectedFolder(folder ?? FOLDER_UNFILED)
      void fetchDiagrams()
    },
    [fetchDiagrams],
  )

  // The sidebar's folder entries, derived from the full (unfiltered) list so
  // counts reflect every diagram regardless of the active search or folder.
  const folderEntries: FolderEntry[] = useMemo(
    () => buildFolderEntries({ diagrams }),
    [diagrams],
  )

  // The real Diagram_Folders in use, offered as choices in the New diagram
  // dialog. The special "All diagrams" and "Unfiled" entries are filtered out
  // by comparing against their sentinel values.
  const existingFolders: string[] = useMemo(
    () =>
      folderEntries
        .filter((entry) => entry.value !== FOLDER_ALL && entry.value !== FOLDER_UNFILED)
        .map((entry) => entry.value),
    [folderEntries],
  )

  /**
   * Open the New diagram dialog, or defer to a supplied `onNewDiagram` handler
   * when the parent provides one (keeping task 5.2's optional override).
   */
  const handleNewDiagram = useCallback((): void => {
    if (onNewDiagram !== undefined) {
      onNewDiagram()
      return
    }
    setNewDialogOpen(true)
  }, [onNewDiagram])

  // What the grid or table actually shows: folder-filtered, search-filtered,
  // and sorted.
  const visibleDiagrams: DiagramSummary[] = useMemo(
    () =>
      selectDiagrams({
        diagrams,
        folder: selectedFolder,
        kind: kindFilter,
        query: searchQuery,
        sort,
      }),
    [diagrams, selectedFolder, kindFilter, searchQuery, sort],
  )

  /**
   * Open a diagram in the editor route (task 5.1 adds `/diagrams/:diagramId`).
   *
   * @param diagram - The diagram to open.
   */
  const handleOpen = useCallback(
    (diagram: DiagramSummary): void => {
      navigate(`/diagrams/${diagram.id}`)
    },
    [navigate],
  )

  /**
   * Build the actions menu for a diagram, used on each grid card and table row.
   * A parent-supplied `renderActions` takes precedence; otherwise the built-in
   * `DiagramActionsMenu` is used once the repo info has loaded (it needs the
   * repo to build GitLab URLs and the Embed_Snippet). A successful delete
   * refreshes the list.
   */
  const effectiveRenderActions = useCallback(
    (diagram: DiagramSummary): React.ReactNode => {
      if (renderActions !== undefined) {
        return renderActions(diagram)
      }
      if (repo === null) {
        return null
      }
      return (
        <DiagramActionsMenu
          diagram={diagram}
          repo={repo}
          getAccessToken={getAccessToken}
          onOpen={handleOpen}
          onDeleted={() => void fetchDiagrams()}
        />
      )
    },
    [renderActions, repo, getAccessToken, handleOpen, fetchDiagrams],
  )

  /**
   * Render the main content area: loading, error, empty, or the grid/table.
   *
   * @returns The content element for the current state.
   */
  const renderContent = (): React.JSX.Element => {
    if (loading) {
      return <LoadingSpinner loadingContent="Loading diagrams..." />
    }

    if (error !== null) {
      return (
        <div className="flex h-full flex-col justify-center">
          <div className="flex w-full justify-center">
            <Item variant="outline" className="w-auto">
              <ItemContent>
                <ItemTitle className="text-destructive">Error: {error}</ItemTitle>
              </ItemContent>
            </Item>
          </div>
        </div>
      )
    }

    // Empty: no diagrams exist at all (Requirement 9.9).
    if (diagrams.length === 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
          <p>No diagrams yet. Create the first one.</p>
        </div>
      )
    }

    // Diagrams exist, but none match the current folder or search.
    if (visibleDiagrams.length === 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
          <p>No diagrams match the current filters.</p>
        </div>
      )
    }

    if (view === "table") {
      return (
        <DiagramTable
          diagrams={visibleDiagrams}
          onOpen={handleOpen}
          renderActions={effectiveRenderActions}
        />
      )
    }

    return (
      <DiagramGrid
        diagrams={visibleDiagrams}
        getAccessToken={getAccessToken}
        onOpen={handleOpen}
        renderActions={effectiveRenderActions}
      />
    )
  }

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <Card className="flex min-h-0 flex-1 flex-col gap-4 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>
                <h1>{PAGE_TITLE}</h1>
              </CardTitle>
              <CardDescription>
                Draw, store, and share diagrams, saved as files in the diagrams repo.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleNewDiagram}>
              <Plus data-icon="inline-start" />
              New diagram
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1">
            <DiagramsSidebar
              folders={folderEntries}
              selectedFolder={selectedFolder}
              onSelectFolder={setSelectedFolder}
            />
            <div className="flex min-w-0 flex-1 flex-col pl-4">
              {/* Toolbar: search on the left, view selector and sort on the right. */}
              <div className="mb-4 flex flex-wrap items-center gap-4">
                <div className="mr-auto">
                  <SearchButton value={searchQuery} onChange={setSearchQuery} />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Filter data-icon="inline-start" className="mr-1.5 size-4" />
                      Kind: {KIND_FILTER_LABELS[kindFilter]}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-auto">
                    <DropdownMenuRadioGroup
                      value={kindFilter}
                      onValueChange={(value) => setKindFilter(parseKindFilter(value))}
                    >
                      <DropdownMenuRadioItem value="all">
                        {KIND_FILTER_LABELS.all}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="excalidraw">
                        {KIND_FILTER_LABELS.excalidraw}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="mermaid">
                        {KIND_FILTER_LABELS.mermaid}
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <ArrowDownUp data-icon="inline-start" className="mr-1.5 size-4" />
                      Sort: {SORT_LABELS[sort]}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-auto">
                    <DropdownMenuRadioGroup
                      value={sort}
                      onValueChange={(value) => setSort(parseSort(value))}
                    >
                      <DropdownMenuRadioItem value="updated">
                        {SORT_LABELS.updated}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="title">
                        {SORT_LABELS.title}
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <ViewToggle value={view} onChange={setView} />
              </div>
              <div className="min-h-0 flex-1">{renderContent()}</div>
            </div>
          </div>
        </CardContent>
      </Card>
      <NewDiagramDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        existingFolders={existingFolders}
        onImportedSeveral={handleImportedSeveral}
      />
    </>
  )
}
